import type { GlMapHideOperation } from '@festina/configuration-general-types/configuration'
import type { LayerKey } from '@festina-life/api'
import { dateUtils, type Optional } from '@festina-life/core'
import {
  FormikDatePicker,
  FormikForm,
  FormikTextInput,
} from '@festina-life/ui/formikForms'
import { Checkbox } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useState } from 'react'

import { useOperationsContext } from '../../workspace/operationsContext'
import { draftCreationStatusReporter } from '../draftCreationModal/draftCreationStatusReporter'
import type {
  DraftCreatedResult,
  DraftSubmitStatus,
} from '../draftCreationModal/types'
import { useManageGlMapDraftViewDate } from '../moveViewDate/useManageGlMapDraftViewDate'
import { validateKey } from './helpers'
import type { CreateGlMapHideDraftInput } from './types'
import { useGlMapHideDraftValidationSchema } from './useGlMapHideDraftValidationSchema'

type Props = {
  formId: string
  layer: LayerKey
  block: string
  initialValue?: Optional<Partial<CreateGlMapHideDraftInput>>
  onStatusChanged: (status: DraftSubmitStatus) => void
}

export const GlMapHideOperationForm = ({
  formId,
  layer,
  block,
  initialValue,
  onStatusChanged,
}: Props) => {
  const translate = useTranslate()
  const { onAddOrUpdateOperation } = useOperationsContext()
  const validationSchema = useGlMapHideDraftValidationSchema({ layer, block })
  const { isViewDateActiveFor, moveViewDateOnto } = useManageGlMapDraftViewDate(
    {
      layer,
      block,
    },
  )

  const isMappingIdFixed = !!initialValue?.mappingId
  const initialValues = {
    mappingId: initialValue?.mappingId ?? '',
    fromDate: initialValue?.fromDate
      ? dateUtils.dateAsServerStr(initialValue.fromDate)
      : undefined,
  } satisfies CreateGlMapHideDraftInput

  const [shouldMoveDate, setShouldMoveDate] = useState(
    !isViewDateActiveFor(initialValues.fromDate, initialValues.mappingId),
  )

  const handleSubmit = async (
    input: CreateGlMapHideDraftInput,
  ): Promise<DraftCreatedResult> => {
    const hideOperation = {
      _type: 'GlMapHideOperation',
      mappingId: input.mappingId,
      fromDate: input.fromDate,
    } satisfies GlMapHideOperation

    const createdOnWorkspaceKey = await onAddOrUpdateOperation({
      operation: hideOperation,
      layer,
      block,
      shouldOpenUnifiedBranchWorkspace: true,
    })

    if (shouldMoveDate) {
      moveViewDateOnto(input.fromDate, input.mappingId)
    }

    return {
      createdDraft: {
        type: 'glMap',
        name: input.mappingId,
        fromDate: input.fromDate
          ? dateUtils.dateUtcAsLocal(input.fromDate).toDate()
          : undefined,
        layer,
        block,
      },
      addedToWorkspaceKey: createdOnWorkspaceKey,
    }
  }

  return (
    <FormikForm
      formId={formId}
      initialValues={initialValues}
      onSubmit={draftCreationStatusReporter(onStatusChanged, handleSubmit)}
      validationSchema={validationSchema}
    >
      {({ values }) => (
        <>
          <FormikTextInput
            label={translate('glMapDraftForm.mappingId.label')}
            name={validateKey('mappingId')}
            disabled={isMappingIdFixed}
          />
          <FormikDatePicker
            label={translate('glMapDraftForm.hideFromDate.label')}
            name={validateKey('fromDate')}
            isRequired={false}
          />
          {!isViewDateActiveFor(values.fromDate, values.mappingId) && (
            <Checkbox
              wrapperProps={{
                label: translate('glMapDraftForm.moveViewAtLabel'),
                description: translate('glMapDraftForm.moveViewAtDescription'),
              }}
              checked={shouldMoveDate}
              onChange={setShouldMoveDate}
            />
          )}
        </>
      )}
    </FormikForm>
  )
}
